import React, { useState, useEffect } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import { ITicket } from "../types/Ticket/Ticket";
import { useSearchParams } from "react-router-dom";
import { userApi } from "../redux/api/userApi";
import { clientApi } from "../redux/api/clientApi";
import { ComboBox, ComboBoxFilterChangeEvent, DropDownList, ListItemProps } from "@progress/kendo-react-dropdowns";
import { IAccount } from "../types/User/IAccount";
import { IUserResult } from "../types/User/IUserResult";
import { ticketApi } from "../redux/api/ticketApi";
import { Spinny } from "./Spinny";
import TicketLogSuccessful from "./TicketLogSuccessful";
import useTenantUrl from "./hooks/useTenantUrl";
import { Button } from "@progress/kendo-react-buttons";

const TicketForm: React.FC = () => {
    // Debounce hook: returns a debounced version of a value after `delay` ms
    function useDebouncedValue<T>(value: T, delay = 200) {
        const [debounced, setDebounced] = useState<T>(value);
        useEffect(() => {
            const handle = setTimeout(() => setDebounced(value), delay);
            return () => clearTimeout(handle);
        }, [value, delay]);
        return debounced;
    }
    const [searchParams] = useSearchParams();
    const description = searchParams.get("description") || "";
    //const revTenantId = searchParams.get("revTenantId") || "";
    const messageFromUserId = searchParams.get("fromUserId") || "";
    const [user, setUser] = useState<IAccount | null>(null);
    const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
    const [comboValue, setComboValue] = useState<any | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebouncedValue<string>(searchTerm, 500);
    const [triggerGetClients] = clientApi.useLazyGetClientsQuery();
    const [contacts, setContacts] = useState<Array<{ id: string; name: string }>>([]);
    const [ticket, setTicket] = useState<ITicket>({ Initial_Action: description } as ITicket);
    const [findAccount] = userApi.useFindAccountMutation();
    const userAccountId = user?.UId || "";
    const { data: foundUserAccount } = userApi.useGetUserQuery(userAccountId, { skip: !userAccountId });
    const { data: profile } = userApi.useGetProfileQuery();
    const { data: searchUsers, isLoading: searchUsersLoading } = userApi.useSearchUsersQuery(debouncedSearchTerm, { skip: debouncedSearchTerm.length < 3 });
    const { data: projects, isLoading: projectsLoading } = clientApi.useGetProjectListQuery(ticket.Company_Id, { skip: !ticket.Company_Id });
    const { data: ticketStatuses } = ticketApi.useGetStatusesQuery(1);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [saveTicket, { isLoading: isSaving, error }] = ticketApi.useLogTicketMutation();

    const tenantUrl = useTenantUrl();

    useEffect(() => {

        console.log("Attempting to find user account with tenant ID and message from user ID:", "messageFromUserId=", messageFromUserId, profile);
        if (profile) {

            //No valid from user  - use current user profile
            if (!messageFromUserId) {
                const userItem = { ID: Number(profile?.Id), Full_Name: profile?.F_Name + " " + profile?.L_Name, Profile_Image_Path: profile?.Profile_Image_Url, Company_Id: profile?.Company_Id } as IUserResult;

                console.log("No messageFromUserId, using profile as user account:", userItem);
                setComboValue(userItem);
                setTicket(prev => ({ ...prev, Company_Id: profile?.Company_Id ?? prev.Company_Id }));
                return;
            }

            console.log('Find the current teams user in Revelation');

            //Find the current teams user in Revelation
            findAccount({ tenantUid: '', userId: messageFromUserId }).unwrap().then((result) => {
                console.log("findAccount result:", result);

                if (!result) {
                    //Use current user (profile)
                    //setUser({ UserId: profile?.Id || "", UserName: profile?.F_Name + " " + profile?.L_Name, TenantUId: revTenantId,: profile?.Profile_Image_Path } as IAccount);
                    const userItem = { ID: Number(profile?.Id), Full_Name: profile?.F_Name + " " + profile?.L_Name, Profile_Image_Path: profile?.Profile_Image_Url, Company_Id: profile?.Company_Id } as IUserResult;
                    setComboValue(userItem);
                    setTicket(prev => ({ ...prev, Company_Id: profile?.Company_Id ?? prev.Company_Id }));
                    return;
                }

                setUser(result);
            }).catch((err) => {
                console.error("findAccount error:", err);
                const userItem = { ID: Number(profile?.Id), Full_Name: profile?.F_Name + " " + profile?.L_Name, Profile_Image_Path: profile?.Profile_Image_Url, Company_Id: profile?.Company_Id } as IUserResult;
                setComboValue(userItem);
                setTicket(prev => ({ ...prev, Company_Id: profile?.Company_Id ?? prev.Company_Id }));
            });
        } else {
            console.log("No revTenantId or profile, cannot find user account. revTenantId:", "profile:", profile);
        }
    }, [messageFromUserId, findAccount, profile]);

    //Load the found user's company ID
    useEffect(() => {
        if (foundUserAccount) {
            setTicket(prev => ({ ...prev, Company_Id: prev.Company_Id ?? foundUserAccount.Company_Id }));
        }
    }, [foundUserAccount]);

    //load the default statuses for the status combo box on mount
    useEffect(() => {
        if (ticketStatuses) {
            const defaultStatus = ticketStatuses.find(s => s.Open_default);
            if (defaultStatus) {
                setTicket(prev => ({ ...prev, Call_Status_Id: defaultStatus.ID }));
            }
        }
    }, [ticketStatuses]);


    useEffect(() => {
        // If we have a user account, show it as the first option in the combo box.
        if (user) {
            console.log('User account found:', user);
            const userItem = { ID: Number(user.UserId), Full_Name: user.UserName, Profile_Image_Path: user.UserProperties?.picture } as IUserResult;
            setComboValue(userItem);
        }
    }, [user]);

    //default project when client selected
    useEffect(() => {
        if (projects && projects.length > 0 && !ticket.Project_Id) {
            console.log("Projects loaded:", projects, ticket.Project_Id);
            var defaultProject = projects.find(p => p.Primary_Project);
            setTicket((prev) => ({ ...prev, Project_Id: defaultProject ? defaultProject.ID : projects[0].ID }));
        }
    }, [projects, ticket.Project_Id]);

    const handleSubmit = async (ev?: React.FormEvent) => {
        ev?.preventDefault();


        // If running inside a Teams task module or message extension submit, use submitTask
        try {


            ticket.Company_Contact = comboValue?.ID;

            //Log via API then just close the task mode so we can do error handling there if needed, and avoid issues with server side errors
            var newTicket = await saveTicket(ticket).unwrap();

            setTicket((prev) => ({ ...prev, ID: newTicket.ID }));
            setShowSuccessDialog(true);



        } catch (err) {

            // If teams sdk not available, fall back to console log or future API call
            // eslint-disable-next-line no-console
            console.log("Ticket submit payload:", ticket);
            // Optionally close a popup window if opened externally
            if (window && (window as any).close) (window as any).close();
        }
    };

    const closeDialog = async () => {
        await microsoftTeams.app.initialize();
        // Optionally close a popup window if opened externally
        //Close the Teams dialog
        microsoftTeams.dialog.url.submit();
    }


    return (
        <div className="ticket-form-container p-2" >
            {isSaving && <Spinny text="Saving..." />}
            {showSuccessDialog && <TicketLogSuccessful ticketPhrase={profile ? profile?.Phrases.CallPhrase : 'Ticket'} ticketId={ticket?.ID} open={showSuccessDialog} onClose={() => closeDialog()} webBaseUrl={tenantUrl ?? ""} />}
            <form className="m-1" id="ticketForm" onSubmit={handleSubmit}>
                <h5 style={{ fontWeight: 600 }}>Log a new {profile ? profile?.Phrases.CallPhrase : 'Ticket'}</h5>
                {error && <div className="alert alert-danger" role="alert">
                    Error while saving ticket: {JSON.stringify(error)}
                </div>}
                <div style={{ marginBottom: 8, marginTop: 24 }}>
                    <label className="label">{profile ? profile?.Phrases.EndUser : 'End User'}: </label>
                    <ComboBox
                        required
                        size={"large"}
                        id="contactCombo"
                        className="form-control input-field contact-combo"
                        loading={searchUsersLoading}
                        placeholder="Type 3 or more characters to search"
                        data={searchUsers?.slice().sort((a, b) => a.Full_Name?.localeCompare(b.Full_Name)) || []}
                        textField="Full_Name"
                        value={comboValue}
                        filterable={true}
                        onChange={(e: any) => {
                            const val = e.target.value;
                            setComboValue(val);
                            // if the selected item is a real client (not the user placeholder), set Company_Id
                            if (val && !val.isUser) {
                                setTicket({ ...ticket, Company_Id: Number(val.Company_Id) });
                            }
                        }}
                        onFilterChange={(e: ComboBoxFilterChangeEvent) => {
                            const f = e.filter?.value || "";
                            setSearchTerm(f);
                        }}

                        valueRender={(element) => comboValue ? (
                            <div className="w-100 d-flex align-items-center ms-1" >
                                {comboValue?.Profile_Image_Path ? (
                                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                    <img src={comboValue.Profile_Image_Path} alt="profile" style={{ width: 28, height: 28, borderRadius: 14 }} />
                                ) : <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: "#ccc", color: "#000", display: "flex", alignItems: "center", justifyContent: "center" }} >
                                    {comboValue?.Full_Name ? `${comboValue?.Full_Name.substring(0, 1).toUpperCase()}${comboValue?.Full_Name.split(" ")[1]?.substring(0, 1).toUpperCase() || ""}` : "?"}
                                </div>}
                                <span style={{ marginLeft: 8, width: "100%" }}>{element}</span>
                            </div>
                        ) : (
                            element
                        )}
                        itemRender={(li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
                            const item = itemProps.dataItem as IUserResult;
                            const itemChildren = (
                                <div className="" style={{ display: "flex", alignItems: "center" }}>
                                    {item.Profile_Image_Path ? (
                                        // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                        // client-side validation
                                        <img src={item.Profile_Image_Path} alt="profile" style={{ width: 28, height: 28, borderRadius: 14 }} />
                                    ) : <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: "#ccc", color: "#000", display: "flex", alignItems: "center", justifyContent: "center" }} >
                                        {item.Full_Name ? `${item.Full_Name.substring(0, 1).toUpperCase()}${item.Full_Name.split(" ")[1]?.substring(0, 1).toUpperCase() || ""}` : "?"}
                                    </div>}
                                    <span style={{ marginLeft: 8 }}>{item.Full_Name} - {item.Company_Name}</span>
                                </div>)
                            return React.cloneElement(li, li.props, itemChildren);
                        }}
                    />
                </div>

                <div style={{ marginBottom: 8 }}>
                    <label className="label">{profile?.Phrases?.Project || "..."}: </label>
                    <DropDownList
                        id="projectCombo"
                        className="form-control input-field project-combo"
                        data={projects}
                        textField="Project_Name"
                        dataItemKey="ID"
                        value={projects?.find(p => p.ID === ticket.Project_Id)}
                        onChange={(e) => setTicket({ ...ticket, Project_Id: e.value.ID })}
                        required
                        loading={projectsLoading}
                    />
                </div>

                <div style={{ marginBottom: 8 }}>
                    <label className="label">Status: </label>
                    <DropDownList
                        id="statusCombo"
                        className="form-control input-field status-combo"
                        data={ticketStatuses}
                        textField="Call_Status"
                        dataItemKey="ID"
                        value={ticket.Call_Status_Id ? ticketStatuses?.find(status => status.ID === ticket.Call_Status_Id) : null}
                        onChange={(e) => setTicket({ ...ticket, Call_Status_Id: e.value.ID })}
                        required
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label htmlFor="description"><strong>Description</strong></label>
                    <input type="text"
                        id="description"
                        className="form-control input-field"
                        value={ticket.Call_Desc}
                        onChange={e => setTicket({ ...ticket, Call_Desc: e.target.value })}
                        required
                        maxLength={500}
                    />
                </div>

                <div style={{ marginBottom: 8 }}>
                    <label className="label">Notes: </label>
                    <Editor value={ticket.Initial_Action}
                        defaultEditMode="div"
                        onChange={e => setTicket({ ...ticket, Initial_Action: e.html })}
                        tools={[
                            [EditorTools.Bold, EditorTools.Italic, EditorTools.Underline],
                            [EditorTools.BulletedList, EditorTools.NumberedList],
                            [EditorTools.Link, EditorTools.Unlink]
                        ]}
                        style={{ height: 350 }}
                    />
                </div>

                <div className="d-flex justify-content-end">
                    <Button fillMode={"solid"} size={"large"} themeColor="primary" type="submit">
                        Submit
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default TicketForm;
